import { formatMoney } from '@squareup/formatter';
import type { Money } from '@squareup/shared-types-protos/squareup/common/currency';
import { CurrencyCode } from '@squareup/shared-types-protos/squareup/common/currency_codes';
import { type Money as ConnectV2Money, Currency } from '@squareup/shared-types-protos/squareup/connect/v2/common/money';
import CURRENCY_CODE_INFO from './currency-code-info';

export type MoneyUnion = Money | ConnectV2Money;

interface FormatCurrencyProps {
  currencyValue?: MoneyUnion;
  includeMinor?: boolean;
  useGrouping?: boolean;
  useCompact?: boolean;
  locale?: string;
}

function getCurrencyCodeString(value: MoneyUnion): string {
  if ('currencyCode' in value && value.currencyCode !== undefined) {
    return CurrencyCode[value.currencyCode];
  }
  if ('currency' in value && value.currency !== undefined) {
    return Currency[value.currency];
  }
  throw new Error('Invalid money object: missing currency');
}

/*
 * Behind the scene, this is handled by Intl.NumberFormat
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/NumberFormat
 *
 * @param {squareup.common.currency.Money | squareup.connect.v2.common.Money} [currencyValue]
 *  Instance of common money proto
 *
 * @param {Boolean} [includeMinor]
 *  If formatting should include minor units, for dollars this would be cents.
 *  defaults to true because dropping cents should not happen on accident.
 *
 * @param {Boolean} [useGrouping]
 *  If formatting should include separators, for USD this would be commas.
 *  defaults to true to match Intl.NumberFormat.
 *
 * @param {Boolean} [useCompact]
 *  If true, formats the value in compact notation and strips the cents from the return value.
 *  Example: returns "$10" instead of "$10.00" when true.
 *  Defaults to false.
 *
 * @param {Boolean} [locale]
 *  The locale to format the currency in.
 *
 */
export const formatCurrency = ({
  currencyValue,
  includeMinor = true,
  useGrouping = true,
  useCompact = false,
  locale,
}: FormatCurrencyProps): string => {
  if (!currencyValue) {
    return '';
  }

  // Decide if we need to convert the amount into a decimal. This is required for currencies that use cents (e.g. USD, AUD).
  const currencyCodeString = getCurrencyCodeString(currencyValue);
  const currencyCodeInfo = CURRENCY_CODE_INFO[currencyCodeString];

  // Decide if we should include cents in the output ("$10.00" vs "$10"). This is a no-op for currencies that don't have cents (e.g. JPY).
  const maximumFractionDigits = includeMinor ? currencyCodeInfo.minorUnit : 0;

  // Set the minimum fraction digits to be the same as the maximum for consistency in rendering.
  const minimumFractionDigits = maximumFractionDigits;

  return formatMoney(currencyValue, locale, {
    notation: useCompact ? 'compact' : 'standard',
    minimumFractionDigits,
    maximumFractionDigits,
    useGrouping,
    currencySign: 'standard',
  });
};
